import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import style from './ZingNotifications.module.css'
import {
    BATTERY_STALE_MS,
    isZingNotificationDevice,
} from '../../../utils/zingNotifications'

const COUNT_MS = 700

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

// Eases the shown value towards the latest reading, so the number and the
// fill move together (counts up from 0 when the pill first appears).
function useCountTo(target) {
    const [value, setValue] = useState(0)
    const valueRef = useRef(0)

    useEffect(() => {
        if (target == null) return undefined
        const from = valueRef.current
        if (from === target || prefersReducedMotion()) {
            valueRef.current = target
            setValue(target)
            return undefined
        }
        let frame
        const start = performance.now()
        const step = (now) => {
            const t = Math.min((now - start) / COUNT_MS, 1)
            const eased = 1 - Math.pow(1 - t, 3)
            valueRef.current = Math.round(from + (target - from) * eased)
            setValue(valueRef.current)
            if (t < 1) frame = requestAnimationFrame(step)
        }
        frame = requestAnimationFrame(step)
        return () => cancelAnimationFrame(frame)
    }, [target])

    return value
}

// Battery knob drawn on the right half of the connection button, fed by the firmware's
// once-a-minute battery notification (no polling of the device).
function BatteryIndicator() {
    const battery = useSelector((state) => state.zingNotification.battery)
    const isConnected = useSelector((state) => state.webSerial.isConnected)
    const device = useSelector((state) => state.device)
    const [, forceRender] = useState(0)
    const shown = useCountTo(battery?.percent)

    // Re-render once when the current reading goes stale so it is hidden
    useEffect(() => {
        if (!battery) return undefined
        const remaining = battery.receivedAt + BATTERY_STALE_MS - Date.now()
        const timer = setTimeout(() => forceRender((n) => n + 1), Math.max(remaining, 0) + 50)
        return () => clearTimeout(timer)
    }, [battery])

    if (
        !battery ||
        !isConnected ||
        !isZingNotificationDevice(device.connectedDevice, device.deviceVersion) ||
        Date.now() - battery.receivedAt > BATTERY_STALE_MS
    )
        return null

    // Colour follows the real reading, not the count-up, so it never flashes
    // red on the way up to a healthy level.
    const { percent } = battery
    const level =
        percent <= 10
            ? `${style.batteryLow} ${style.batteryCritical}`
            : percent <= 20
              ? style.batteryLow
              : percent <= 50
                ? style.batteryMid
                : style.batteryOk
    return (
        <div
            className={`${style.batteryPill} ${level}`}
            title={`Zing battery ${percent}%${percent <= 20 ? ' - low, please charge' : ''}`}
            data-testid="zing-battery"
        >
            <span className={style.batteryRow}>
                <span className={style.batteryBody}>
                    <span
                        className={style.batteryFill}
                        style={{ width: `${shown}%` }}
                    />
                    <span className={style.batteryCells} />
                </span>
                <span className={style.batteryCap} />
            </span>
            <span key={percent} className={style.batteryText}>
                {shown}%
            </span>
        </div>
    )
}

export default React.memo(BatteryIndicator)
